
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useTenant } from './providers/TenantProvider';
import Layout from './components/Layout';
import MemberPortalLayout from './components/MemberPortalLayout';
import { features } from './features';

// Pages
import { AuthPage } from './pages/AuthPage';
import { DashboardPage } from './pages/DashboardPage';
import { PlansPage } from './pages/PlansPage';
import { SettingsBillingPage } from './pages/SettingsBillingPage';
import { MembersListPage } from './pages/MembersListPage';
import { MemberProfilePage } from './pages/MemberProfilePage';
import { CheckInPage } from './pages/CheckInPage';
import { SetupPage } from './pages/SetupPage';
import LeadsPipelineBoard from './pages/LeadsPipelineBoard';
import LeadDetailPage from './pages/LeadDetailPage';
import InvoicesPage from './pages/InvoicesPage';
import { UsersPage } from './pages/UsersPage';
import { BookingsPage } from './pages/BookingsPage';
import { ServicesPage } from './pages/ServicesPage';
import { ProviderSchedulePage } from './pages/ProviderSchedulePage';
import { ServiceFormPage } from './pages/ServiceFormPage';
import { AppointmentsPage } from './pages/AppointmentsPage';
import { WebsiteDashboardPage } from './pages/WebsiteDashboardPage';
import { WebsiteOnboardingPage } from './pages/WebsiteOnboardingPage';
import UiKitPage from './pages/UiKitPage';
import { BookingPage } from './pages/public/BookingPage';
import { BookingProviderPage } from './pages/public/BookingProviderPage';
import { BookingTimePage } from './pages/public/BookingTimePage';
import { BookingContactPage } from './pages/public/BookingContactPage';
import { AppointmentManagePage } from './pages/public/AppointmentManagePage';
import { MemberDashboardPage } from './pages/member/MemberDashboardPage';
import { MemberBookPage } from './pages/member/MemberBookPage';
import { MemberAppointmentsPage } from './pages/member/MemberAppointmentsPage';

const adminRoles = ['owner', 'manager', 'staff', 'coach'];

function App() {
  const { loading, user, tenantId, roles } = useTenant();
  const isAuthenticated = !!user;
  const hasAdminAccess = !tenantId || roles.some((role) => adminRoles.includes(role));
  const hasMemberAccess = roles.includes('member');
  const defaultAuthenticatedPath = hasAdminAccess ? '/dashboard' : hasMemberAccess ? '/member' : '/setup';

  const adminRoute = (element: any) => {
    if (!isAuthenticated) return <Navigate to="/" replace />;
    if (!hasAdminAccess) return <Navigate to={hasMemberAccess ? '/member' : '/setup'} replace />;
    return <Layout>{element}</Layout>;
  };

  const memberRoute = (element: any) => {
    if (!isAuthenticated) return <Navigate to="/" replace />;
    if (!hasMemberAccess) return <Navigate to={hasAdminAccess ? '/dashboard' : '/setup'} replace />;
    return <MemberPortalLayout>{element}</MemberPortalLayout>;
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Routes>
      {/* Auth Route */}
      <Route
        path="/"
        element={isAuthenticated ? <Navigate to={defaultAuthenticatedPath} replace /> : <AuthPage />}
      />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={adminRoute(<DashboardPage />)}
      />
      <Route
        path="/plans"
        element={adminRoute(<PlansPage />)}
      />
      <Route
        path="/settings/billing"
        element={adminRoute(<SettingsBillingPage />)}
      />
      <Route
        path="/members"
        element={adminRoute(<MembersListPage />)}
      />
      <Route
        path="/members/:memberId"
        element={adminRoute(<MemberProfilePage />)}
      />
      <Route
        path="/checkin"
        element={adminRoute(<CheckInPage />)}
      />
      <Route
        path="/setup"
        element={isAuthenticated ? <SetupPage /> : <Navigate to="/" replace />}
      />
      <Route
        path="/leads"
        element={adminRoute(<LeadsPipelineBoard />)}
      />
      <Route
        path="/leads/:leadId"
        element={adminRoute(<LeadDetailPage />)}
      />
      <Route
        path="/invoices"
        element={adminRoute(<InvoicesPage />)}
      />
      <Route
        path="/bookings"
        element={adminRoute(<BookingsPage />)}
      />
      <Route
        path="/services"
        element={adminRoute(<ServicesPage />)}
      />
      <Route
        path="/services/new"
        element={adminRoute(<ServiceFormPage />)}
      />
      <Route
        path="/services/:id/edit"
        element={adminRoute(<ServiceFormPage />)}
      />
      <Route
        path="/users"
        element={adminRoute(<UsersPage />)}
      />
      <Route
        path="/website"
        // isAuthenticated WebsiteDashboardPage protected via adminRoute.
        element={adminRoute(<WebsiteDashboardPage />)}
      />
      <Route
        path="/website/onboarding"
        element={adminRoute(<WebsiteOnboardingPage />)}
      />
      <Route
        path="/schedules/:providerId"
        element={adminRoute(<ProviderSchedulePage />)}
      />
      <Route
        path="/appointments"
        element={adminRoute(<AppointmentsPage />)}
      />
      {features.uiKit && (
        <Route
          path="/ui-kit"
          element={adminRoute(<UiKitPage />)}
        />
      )}

      {/* Member Portal Routes - Auth required */}
      <Route
        path="/member"
        element={memberRoute(<MemberDashboardPage />)}
      />
      <Route
        path="/member/book"
        element={memberRoute(<MemberBookPage />)}
      />
      <Route
        path="/member/appointments"
        element={memberRoute(<MemberAppointmentsPage />)}
      />

      {/* Public Booking Routes - No auth required */}
      <Route path="/book/:tenantSlug" element={<BookingPage />} />
      <Route path="/book/:tenantSlug/service/:serviceId" element={<BookingProviderPage />} />
      <Route path="/book/:tenantSlug/service/:serviceId/provider/:providerId" element={<BookingTimePage />} />
      <Route path="/book/:tenantSlug/service/:serviceId/provider/:providerId/contact" element={<BookingContactPage />} />
      <Route path="/appointment/:publicAccessHash" element={<AppointmentManagePage />} />

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

export default App;
